import { defineAsyncComponent, ref } from "vue";
import { useForm } from "vee-validate";
import { useReCaptcha } from 'vue-recaptcha-v3'
import IMask from "imask";

export default {
    props: {
        formId: {
            type: String
        },
        formGuid: {
            type: String
        },
        formElements: {
            type: Array,
            default: () => []
        },
        showSummarizedData: {
            type: Boolean,
        },
        confirmationMessage: {
            type: String
        },
        submitSuccessMessage: {
            type: String,
            default: "Formularen er blevet sendt!"
        },
        submitErrorMessage: {
            type: String,
            default: "Der opstår en fejl med insendt!"
        },
        resetAfterSubmission: {
            type: Boolean
        },
    },
    setup(props) {
        const fieldPrefix = '';
        const submissionSucceeded = ref(false);
        const submitting = ref(false);
        const showStatusMessage = ref(false);
        const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();
        const elementBasicProps = ['label', 'validators'];

        // Cache for components to avoid re-importing which causes infinite reactivity updates on the forms 'values'
        const componentCache = {};

        const getElementProps = (element) => {
            const { type, ...rest } = element.properties;
            const elementType = getTypeAttribute(element.contentType);
            return ({
                name: `${fieldPrefix + element.displayName}`,
                required: isRequired(element.properties),
                ...type ? { type: elementType } : {},
                ...extraElementSpecificProps(element, rest)
            });
        };

        const getComponentType = (element) => {
            const componentMap = {
                CustomFileUploadElementBlock: "NykInputFile",
                CustomDateTimeElementBlock: "NykDateTimePicker",
                CustomParagraphElementBlock: 'NykFormParagraph',
                CustomTitleElementBlock: 'NykFormTitle',
                CustomTextboxElementBlock: "NykInput",
                CustomTextareaElementBlock: "NykTextarea",
                CustomAddressElementBlock: element.properties?.autoCompleteAddress ? "NykAddress" : "NykInput",
                CustomChoiceElementBlock: element.properties.allowMultiSelect ? "NykCheckboxGroup" : "NykRadioGroup",
                CustomPhoneElementBlock: "NykPhone",
                CustomCvrNumberElementBlock: "NykCvr",
                CustomSubmitButtonElementBlock: "NykSubmitButton",
                CustomUrlQueryParameterHiddenElementBlock: "NykUrlQueryParameterHiddenElement"
            };

            const componentName = componentMap[element.contentType] || "NykInput";

            if (!componentCache[componentName]) {
                componentCache[componentName] = defineAsyncComponent(() =>
                    import(`../${componentName}/${componentName}.vue`)
                );
            }

            return componentCache[componentName];
        };

        const getElementEvents = (element) => {
            if (element.contentType === "CustomCvrNumberElementBlock") {
                return {
                    update: onFieldUpdate
                }
            }
            return {};
        };

        const getTypeAttribute = (contentType) => {
            if (contentType === "CustomNumberElementBlock") {
                return 'text'
            }


            if (contentType === "CustomDateTimeElementBlock") {
                return 'datetime-local'
            }

            return undefined;
        };

        const isRequired = (elementProperties) => {
            const validators = elementProperties.validators;

            if (validators) {
                return !!validators.find(validator => validator.type === "RequiredValidator");
            }

            return undefined;
        };

        const { handleSubmit, values, meta, resetForm, setFieldValue } = useForm();

        const onSubmit = handleSubmit(async formValues => {
            const isConfirmed = showConfirmationSummary(formValues);

            if (!isConfirmed) {
                return;
            }

            submitting.value = true;
            showStatusMessage.value = false;
            submissionSucceeded.value = false;

            let meta = { formId: props.formId, formGuid: props.formGuid, recaptchaToken: await getRecatpchaToken() }

            try {
                const response = await fetch('http://localhost:7158/api/upload/files', {
                    method: 'POST',
                    body: getFormData(formValues, meta),
                });

                const data = await response.json();
                submissionSucceeded.value = data.success;
            }
            catch (error) {
                submissionSucceeded.value = false;
                console.error("Error submitting form:", error);
            }
            finally {
                if (props.resetAfterSubmission) {
                    resetForm();
                }

                submitting.value = false;
                showStatusMessage.value = true;
            }
        });

        const showConfirmationSummary = (formValues) => {
            if (props.showSummarizedData) {
                const summary = Object.entries(formValues)
                    .map(([key, value]) => {
                        if (Array.isArray(value) && value.length && value[0] instanceof File) {
                            return `${key}: ${value.map(f => f.name).join(', ') || '-'}`;
                        }
                        return `${key}: ${value || '-'}`;
                    })
                    .join('\n');


                const message = `${props.confirmationMessage || 'Bekræft venligst dine oplysninger:'}\n\n${summary}\n\nVil du fortsætte?`;
                const confirmed = window.confirm(message);

                if (!confirmed) {
                    return false;
                }
            }

            return true
        };

        const getFormData = (formValues, meta) => {
            const formData = new FormData();

            const formularData = Object.entries(formValues).reduce((data, [name, value]) => {
                const normalizedName = name.replace(fieldPrefix, "");
                const element = props.formElements.find(el => el.displayName === name);
                const key = element ? element.key : normalizedName;
                let valueType = Array.isArray(value) ? 'array' : typeof value;

                if (element.contentType === 'DateTimeElementBlock') {
                    valueType = 'date';
                }

                if (valueType === 'array' && value.length !== 0 && value[0] instanceof File) {
                    const files = Array.from(value);
                    files.forEach(file => formData.append('file', file, file.name));
                }

                return [
                    ...data,
                    {
                        key,
                        name: normalizedName,
                        label: element ? element.properties.label : '',
                        value: ['object', 'array', 'boolean', 'number'].includes(valueType)
                            ? JSON.stringify(value)
                            : value,
                        type: valueType
                    }]
            }, []);

            const fullData = {
                formId: meta.formId,
                formGuid: meta.formGuid,
                recaptchaToken: meta.recaptchaToken,
                formularData: formularData
            };

            formData.append('formData', JSON.stringify(fullData));
            return formData;
        };


        const getRecatpchaToken = async () => {
            await recaptchaLoaded()
            return await executeRecaptcha('login')
        }

        const checkVisibilityCondition = (condition) => {
            const comparingFieldName = `${fieldPrefix}${condition.field}`;
            const valueToCompareAgainst = condition.fieldValue;

            if (condition.operator === "Equals") {
                return values[comparingFieldName] === valueToCompareAgainst;
            }

            if (condition.operator === "NotEquals") {
                return values[comparingFieldName] !== valueToCompareAgainst;
            }

            if (condition.operator === "Contains") {
                return values[comparingFieldName]?.includes(valueToCompareAgainst);
            }

            if (condition.operator === "NotContains") {
                return !values[comparingFieldName]?.includes(valueToCompareAgainst);
            }

            if (condition.operator === "MatchRegularExpression") {
                const regex = new RegExp(valueToCompareAgainst);
                return regex.test(values[comparingFieldName]);
            }

            return true;
        };

        const isVisible = (element) => {
            if (!element.properties.conditions) return true;

            const conditionsSatisfied = element.properties.conditionCombination === "Any"
                ? element.properties.conditions.some(checkVisibilityCondition)
                : element.properties.conditions.every(checkVisibilityCondition);

            if (element.properties.satisfiedAction && element.properties.satisfiedAction === "hide") {
                return !conditionsSatisfied;
            }

            return conditionsSatisfied;
        };

        const extraElementSpecificProps = (element, rest) => {
            if (element.contentType === "CustomNumberElementBlock") {
                return {
                    format: {
                        mask: Number,
                        ...element.properties.noThousandSeperator ? {} : { thousandsSeparator: ',' },
                        ...element.properties.allowDecimal ? {
                            scale: 2, radix: '.',
                            mapToRadix: ['.'],
                        } : {
                            scale: 0
                        },
                    },
                    inputMode: 'numeric',
                    ...rest
                };
            }
            else if (element.contentType === "CustomCvrNumberElementBlock") {
                const elementProperties = Object.entries(rest);

                const relatedFields = elementProperties
                    .filter(([field]) => !elementBasicProps.includes(field))
                    .reduce((fields, [field, value]) => ({ ...fields, [field]: value }), {});

                rest = elementProperties
                    .filter(([field]) => elementBasicProps.includes(field))
                    .reduce((fields, [field, value]) => ({ ...fields, [`${field}`]: value }), {});

                return {
                    relatedFields,
                    ...rest
                };
            }
            else if (element.contentType === "CustomCprNumberElementBlock") {
                return {
                    format: {
                        mask: 'DDMMYY 0000',
                        blocks: {
                            DD: {
                                mask: IMask.MaskedRange,
                                from: 1,
                                to: 31,
                            },
                            MM: {
                                mask: IMask.MaskedRange,
                                from: 1,
                                to: 12,
                            },
                            YY: {
                                mask: IMask.MaskedRange,
                                from: 0,
                                to: 99,
                            }
                        }
                    },
                    inputMode: 'numeric',
                    ...rest
                };
            }

            else if (element.contentType === "CustomEmailElementBlock") {
                return {
                    ...rest,
                    extraValidationProps: { validDomains: element.properties.validDomains, contentType: element.contentType },
                    type: 'email',
                    validators: [
                        {
                            type: 'EmailFormatValidator',
                            model: {
                                message: element.properties.invalidEmailFormatErrorMessage
                            }
                        },
                        {
                            type: 'EmailDomainValidator',
                            model: {
                                message: element.properties.invalidDomainErrorMessage,
                                validDomains: element.properties.validDomains || []
                            }
                        }
                    ]
                };
            }

            else if (element.contentType === "CustomSubmitButtonElementBlock") {
                return {
                    isParentFormValid: meta.value.valid,
                    submitting: submitting.value,
                    ...rest
                };
            }

            return { ...rest };
        };

        const onFieldUpdate = (updates) => {
            Object.entries(updates).forEach(([field, value]) => setFieldValue(field, value));
        };

        return {
            submissionSucceeded,
            showStatusMessage,
            values,
            isVisible,
            onSubmit,
            getComponentType,
            getElementProps,
            getElementEvents,
            onFieldUpdate
        };
    },
}